import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Loader from '@/components/Loader';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import InputSearch from '@/design_system/InputSearch';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import {
  SparePartType,
  useAddSparePartToArticleAction,
  useGetSparePartTypesForAction,
} from '@/models/articleActionSparePart';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import useDebouncedState from '@/utils/useDebouncedState';

import { SparePartDropdownItem } from './SparePartDropdownItem';

export const SparePartSearchDialog = ({
  isOpen,
  onClose,
  onSparePartCreated,
  actionId,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSparePartCreated: (createdSparePartId: string) => void;
  actionId: string;
}) => {
  const { t } = useLingui();

  return (
    <Dialog
      title={t({ id: 'spare-part-search-dialog.add', message: 'Add a spare part' })}
      isOpen={isOpen}
      onOpenChange={onClose}
      fullHeight
    >
      <SparePartSearchDialogContent actionId={actionId} onSparePartCreated={onSparePartCreated} />
    </Dialog>
  );
};

const SparePartSearchDialogContent = ({
  onSparePartCreated,
  actionId,
}: {
  onSparePartCreated: (createdSparePartId: string) => void;
  actionId: string;
}) => {
  const { t } = useLingui();
  const { article, request } = useArticleContext();
  const [query, debouncedQuery, setQuery] = useDebouncedState<string>('', 500);
  const { data: sparePartTypes, isFetching } = useGetSparePartTypesForAction(
    {
      articleId: article.id,
      actionId,
      query: debouncedQuery || undefined,
    },
    {
      keepPreviousData: true,
    }
  );

  const {
    mutateAsync: addSparePart,
    isPending: isPendingCreateSparePart,
    isSuccess: isSuccessCreateSparePart,
  } = useAddSparePartToArticleAction({
    articleId: article.id,
    requestId: request.id,
    actionId,
  });

  const handleCreateSparePart = async (sparePartType: SparePartType) => {
    if (!sparePartType) return;
    const { id } = await addSparePart({ sparePartType });

    onSparePartCreated(id);
  };

  const isPending = isPendingCreateSparePart || isSuccessCreateSparePart;

  return (
    <main>
      <Stack gap="0.5rem">
        <InputSearch
          aria-label={t({
            id: 'spare-part-search-dialog.add-spare-part.label',
            message: 'Add a spare part',
          })}
          placeholder={t({
            id: 'spare-part-search-dialog.placeholder',
            message: 'Search a spare part to add',
          })}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          isLoading={isFetching || query !== debouncedQuery}
          value={query}
          onChange={setQuery}
        />

        {(!sparePartTypes || sparePartTypes.length === 0) && (
          <InlineText color="secondary" style={{ padding: '0.5rem 0' }}>
            <Trans id="spare-part-search-dialog.no-results">No results matching your search</Trans>
          </InlineText>
        )}

        <SparePartsMenu
          sparePartTypeOptions={sparePartTypes || []}
          onChooseSparePartType={handleCreateSparePart}
          isPending={isPending}
        />
      </Stack>
    </main>
  );
};

const SparePartsMenu = ({
  sparePartTypeOptions,
  onChooseSparePartType,
  isPending,
}: {
  sparePartTypeOptions: SparePartType[];
  onChooseSparePartType: (sparePartType: SparePartType) => void;
  isPending: boolean;
}) => {
  return (
    <Stack>
      {sparePartTypeOptions.map((sparePartType) => (
        <SparePartMenuItem
          key={sparePartType.id}
          sparePartType={sparePartType}
          onChooseSparePartType={onChooseSparePartType}
          isPending={isPending}
        />
      ))}
    </Stack>
  );
};

const SparePartMenuItem = ({
  sparePartType,
  onChooseSparePartType,
  isPending,
}: {
  sparePartType: SparePartType;
  onChooseSparePartType: (sparePartType: SparePartType) => void;
  isPending: boolean;
}) => {
  const [hasClicked, setHasClicked] = useState(false);

  return (
    <Button
      style={{ padding: '0.5rem' }}
      variant="style-less"
      onPress={() => {
        onChooseSparePartType(sparePartType);
        setHasClicked(true);
      }}
    >
      <Stack row gap="0.25rem" style={{ flex: 1 }} alignItems="center">
        <SparePartDropdownItem sparePartType={sparePartType} />
        {isPending && hasClicked && <Loader />}
      </Stack>
    </Button>
  );
};
